"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import type { SportsMatchCardBlock as SportsMatchCardBlockData } from "@/payload-types";
import type { FUSportsMatch } from "fansunited-data-layer";
import { cn } from "@/utilities/core/cn";
import { LiveMatchCard } from "@/components/matches";
import { useArticleDataOptional } from "@/views/article/context/ArticleDataContext";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import { fetchMatchByNativeId } from "@/app/(app)/actions/fetchMatchByNativeId";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_BASIC } from "@/config/siteTypes";
import { deriveSportFromEntityId } from "@/components/ui/admin/MatchSelectDrawer/sport";
import { DetailedMatchCard } from "./DetailedMatchCard";
import { EventCardWrapper } from "./EventCardWrapper";
interface SportsMatchCardBlockProps {
  block: SportsMatchCardBlockData;
  className?: string;
}
export function SportsMatchCardBlock(t0) {
  const $ = _c(9);
  const {
    block,
    className
  } = t0;
  const {
    siteType
  } = useTenantSettings();
  if (siteType === SITE_TYPE_BASIC) {
    return null;
  }
  const matchId = block.matchId;
  const variant = block.variant || "header";
  const defaultMarket = block.defaultMarket ?? "FT_1X2";
  const sport = deriveSportFromEntityId(matchId);
  if (sport && sport !== "football") {
    let t1;
    if ($[0] !== className || $[1] !== matchId || $[2] !== variant) {
      t1 = <EventCardWrapper matchId={matchId} variant={variant} className={className} />;
      $[0] = className;
      $[1] = matchId;
      $[2] = variant;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== className || $[5] !== defaultMarket || $[6] !== matchId || $[7] !== variant) {
    t1 = <FootballMatchCard matchId={matchId} variant={variant} defaultMarket={defaultMarket} className={className} />;
    $[4] = className;
    $[5] = defaultMarket;
    $[6] = matchId;
    $[7] = variant;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function FootballMatchCard(t0) {
  const $ = _c(16);
  const {
    matchId,
    variant,
    defaultMarket,
    className
  } = t0;
  const nav = useNavigation();
  let t1;
  if ($[0] !== matchId || $[1] !== nav) {
    t1 = nav.match(matchId);
    $[0] = matchId;
    $[1] = nav;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matchUrl = t1;
  if (variant === "detailed") {
    let t2;
    if ($[3] !== className) {
      t2 = cn("not-prose mb-6", className);
      $[3] = className;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== matchId || $[6] !== matchUrl) {
      t3 = <DetailedMatchCard matchId={matchId} matchUrl={matchUrl} />;
      $[5] = matchId;
      $[6] = matchUrl;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t2 || $[9] !== t3) {
      t4 = <section className={t2}>{t3}</section>;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t2;
  if ($[11] !== className || $[12] !== defaultMarket || $[13] !== matchId || $[14] !== matchUrl) {
    t2 = <HeaderMatchCard matchId={matchId} matchUrl={matchUrl} defaultMarket={defaultMarket} className={className} />;
    $[11] = className;
    $[12] = defaultMarket;
    $[13] = matchId;
    $[14] = matchUrl;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
function HeaderMatchCard(t0) {
  const $ = _c(28);
  const {
    matchId,
    matchUrl,
    defaultMarket,
    className
  } = t0;
  const articleData = useArticleDataOptional();
  let t1;
  if ($[0] !== articleData || $[1] !== matchId) {
    t1 = articleData?.getMatchData?.(matchId);
    $[0] = articleData;
    $[1] = matchId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const prefetchedMatch = t1;
  const locale = useLocale();
  const {
    hasOdds
  } = useTenantSettings();
  const [clientMatch, setClientMatch] = useState(null);
  const [loading, setLoading] = useState(!prefetchedMatch);
  let t2;
  let t3;
  if ($[3] !== locale || $[4] !== matchId || $[5] !== prefetchedMatch) {
    t2 = () => {
      if (prefetchedMatch || !matchId) {
        return;
      }
      fetchMatchByNativeId(matchId, locale).then(data => {
        setClientMatch(data);
        setLoading(false);
      });
    };
    t3 = [matchId, prefetchedMatch, locale];
    $[3] = locale;
    $[4] = matchId;
    $[5] = prefetchedMatch;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const matchData = prefetchedMatch || clientMatch;
  if (loading && !matchData) {
    let t4;
    if ($[8] !== className) {
      t4 = cn("not-prose mb-6", className);
      $[8] = className;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="animate-pulse rounded-lg bg-muted/30 p-6 h-32" role="status"><span className="sr-only">Loading match...</span></div>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t4) {
      t6 = <section className={t4}>{t5}</section>;
      $[11] = t4;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (!matchData) {
    return null;
  }
  let t4;
  if ($[13] !== matchData || $[14] !== matchId) {
    t4 = {
      ...matchData,
      fansUnitedMatchId: matchId
    };
    $[13] = matchData;
    $[14] = matchId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const matchWithNativeId = t4;
  let t5;
  if ($[16] !== className) {
    t5 = cn("not-prose mb-6", className);
    $[16] = className;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== defaultMarket || $[19] !== hasOdds || $[20] !== matchWithNativeId) {
    t6 = <LiveMatchCard match={matchWithNativeId} variant="embedded" showOdds={hasOdds} market={defaultMarket} />;
    $[18] = defaultMarket;
    $[19] = hasOdds;
    $[20] = matchWithNativeId;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== matchUrl || $[23] !== t6) {
    t7 = <a href={matchUrl} className="block cursor-pointer">{t6}</a>;
    $[22] = matchUrl;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t5 || $[26] !== t7) {
    t8 = <section className={t5}>{t7}</section>;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}